import { notebookDocToThread, readLegacyNotebooks } from './notebookMigration';
import type { ThreadMessage, ThreadMeta } from './threadTypes';

/** Where threads live. Today the browser (IndexedDB); the same four calls
 *  map onto a server persistence API later (docs/18 §7). */
export interface ThreadStore {
  listThreads(): Promise<ThreadMeta[]>;
  loadMessages(id: string): Promise<ThreadMessage[]>;
  saveThread(meta: ThreadMeta, messages: ThreadMessage[]): Promise<void>;
  deleteThread(id: string): Promise<void>;
}

export const MAX_THREADS = 60;
const DB_NAME = 'aleno-maitre-threads';
const DB_VERSION = 1;
const THREADS = 'threads';
const MESSAGES = 'messages';
const META = 'meta';
const MIGRATION_KEY = 'notebookMigration';

export function sortThreads(threads: ThreadMeta[]): ThreadMeta[] {
  return [...threads].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/** Threads beyond the newest MAX_THREADS. */
export function threadsToPrune(threads: ThreadMeta[], max = MAX_THREADS): string[] {
  return sortThreads(threads).slice(max).map(t => t.id);
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(THREADS)) db.createObjectStore(THREADS, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(MESSAGES)) db.createObjectStore(MESSAGES, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: 'key' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

function result<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function committed(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

async function withDb<T>(run: (db: IDBDatabase) => Promise<T>): Promise<T> {
  const db = await openDb();
  try {
    return await run(db);
  } finally {
    db.close();
  }
}

/** Copy the old per-scope notebooks into threads once; the old database stays. */
async function migrateNotebooks(): Promise<void> {
  const done = await withDb(db =>
    result(db.transaction(META, 'readonly').objectStore(META).get(MIGRATION_KEY)),
  );
  if (done) return;
  const converted = (await readLegacyNotebooks())
    .map(notebookDocToThread)
    .filter((t): t is NonNullable<typeof t> => t !== null);
  await withDb(async db => {
    const tx = db.transaction([THREADS, MESSAGES, META], 'readwrite');
    for (const { meta, messages } of converted) {
      tx.objectStore(THREADS).put(meta);
      tx.objectStore(MESSAGES).put({ id: meta.id, messages });
    }
    tx.objectStore(META).put({ key: MIGRATION_KEY, at: new Date().toISOString(), count: converted.length });
    await committed(tx);
  });
}

export function createIndexedDbThreadStore(): ThreadStore {
  let migration: Promise<void> | null = null;
  const ready = () => (migration ??= migrateNotebooks().catch(() => undefined));
  return {
    async listThreads() {
      await ready();
      return withDb(async db =>
        sortThreads(
          (await result(db.transaction(THREADS, 'readonly').objectStore(THREADS).getAll())) as ThreadMeta[],
        ),
      );
    },
    async loadMessages(id) {
      await ready();
      return withDb(async db => {
        const record = (await result(
          db.transaction(MESSAGES, 'readonly').objectStore(MESSAGES).get(id),
        )) as { messages?: ThreadMessage[] } | undefined;
        return record?.messages ?? [];
      });
    },
    async saveThread(meta, messages) {
      await ready();
      await withDb(async db => {
        const tx = db.transaction([THREADS, MESSAGES], 'readwrite');
        tx.objectStore(THREADS).put(meta);
        tx.objectStore(MESSAGES).put({ id: meta.id, messages });
        const all = (await result(tx.objectStore(THREADS).getAll())) as ThreadMeta[];
        for (const id of threadsToPrune(all)) {
          tx.objectStore(THREADS).delete(id);
          tx.objectStore(MESSAGES).delete(id);
        }
        await committed(tx);
      });
    },
    async deleteThread(id) {
      await withDb(async db => {
        const tx = db.transaction([THREADS, MESSAGES], 'readwrite');
        tx.objectStore(THREADS).delete(id);
        tx.objectStore(MESSAGES).delete(id);
        await committed(tx);
      });
    },
  };
}

/** In-memory store for tests and browsers without IndexedDB. */
export function createMemoryThreadStore(seed: Array<{ meta: ThreadMeta; messages: ThreadMessage[] }> = []): ThreadStore {
  const metas = new Map(seed.map(s => [s.meta.id, s.meta]));
  const bodies = new Map(seed.map(s => [s.meta.id, s.messages]));
  return {
    async listThreads() {
      return sortThreads([...metas.values()]);
    },
    async loadMessages(id) {
      return bodies.get(id) ?? [];
    },
    async saveThread(meta, messages) {
      metas.set(meta.id, meta);
      bodies.set(meta.id, messages);
      for (const id of threadsToPrune([...metas.values()])) {
        metas.delete(id);
        bodies.delete(id);
      }
    },
    async deleteThread(id) {
      metas.delete(id);
      bodies.delete(id);
    },
  };
}

export function createThreadStore(): ThreadStore {
  return typeof indexedDB === 'undefined' ? createMemoryThreadStore() : createIndexedDbThreadStore();
}
